/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import type {
  Image,
  RetweetMedia,
  Video,
} from '~publish/legacy/composer/composer/entities/factories'
import { LinkifiedText } from '~publish/legacy/shared-components/LinkifiedText'
import type { LinkType } from '~publish/legacy/shared-components/LinkifiedText/LinkifiedText'
import { parseTwitterLinks } from '~publish/legacy/utils/channels/twitter/helpers'

import { ImagesGrid } from '../../../Common/ImagesGrid'
import { DEFAULT_AVATAR } from '../../../Common/utils'
import { AttachmentVideo } from '../AttachmentVideo'

import styles from './AttachmentRetweet.module.css'

interface AttachmentRetweetData {
  avatarUrl: string
  userName: string
  userDisplayName: string
  text: string
  media?: RetweetMedia[]
}

const AttachmentRetweet = ({
  data,
}: {
  data: AttachmentRetweetData
}): JSX.Element => {
  const { avatarUrl, userName, userDisplayName, text, media } = data
  const [avatarSrc, setAvatarSrc] = useState(avatarUrl || DEFAULT_AVATAR)

  useEffect(() => {
    setAvatarSrc(avatarUrl || DEFAULT_AVATAR)
  }, [avatarUrl])

  // Process media for display
  const images: Image[] = []
  let video: Video | null = null

  if (media && media.length > 0) {
    media.forEach((mediaItem) => {
      if (mediaItem.type === 'photo') {
        images.push({
          url: mediaItem.url,
          width: mediaItem.width,
          height: mediaItem.height,
          altText: null,
          source: null,
          mediaType: 'image',
          originalUrl: null,
        })
      } else if (
        mediaItem.type === 'video' ||
        mediaItem.type === 'animated_gif'
      ) {
        // For videos, create a video object with thumbnail
        video = {
          uploadId: mediaItem.id,
          name: mediaItem.type === 'animated_gif' ? 'GIF' : 'Video',
          duration: mediaItem.duration_ms ? mediaItem.duration_ms / 1000 : 0,
          durationMs: mediaItem.duration_ms || 0,
          size: 0,
          width: mediaItem.width,
          height: mediaItem.height,
          url: mediaItem.url,
          originalUrl: mediaItem.url,
          thumbnail: mediaItem.url, // Use the thumbnail URL
          availableThumbnails: [mediaItem.url],
          thumbOffset: 0,
          mediaType: 'video',
        }
      }
    })
  }

  const hasImages = images.length > 0
  const hasVideo = video !== null

  return (
    <div className={styles.wrapper} data-testid="twitter-preview-retweet">
      <div className={styles.userWrapper} translate="no">
        <img
          className={styles.avatar}
          src={avatarSrc}
          alt={`${userName}'s avatar`}
          onError={(): void => {
            if (avatarSrc !== DEFAULT_AVATAR) setAvatarSrc(DEFAULT_AVATAR)
          }}
        />
        <span className={styles.displayName}>{userDisplayName}</span>
        <span className={styles.username}>@{userName}</span>
      </div>
      <LinkifiedText
        links={parseTwitterLinks(text) as LinkType[]}
        size="mini"
        whitespace="pre-wrap"
        newTab
        className={styles.linkifiedText}
      >
        {text}
      </LinkifiedText>
      {hasImages && (
        <ImagesGrid
          images={images}
          isRowFormat={false}
          maxHeight="150px"
          shouldShowGifTag={false}
          className={styles.imagesGrid}
        />
      )}
      {hasVideo && video && <AttachmentVideo video={video} />}
    </div>
  )
}

export { AttachmentRetweet }
